import { FacingGroup, Packaging } from './job.types';
import { orientationFacing } from './job.helper';
import { generateUniqueId } from '../../helpers/utils';

export enum PogSkuLabel {
  OnPog = 'ON_POG',
  InWorkbench = 'IN_WORKBENCH',
  InClipboard = 'IN_CLIPBOARD',
  Trashed = 'TRASHED',
  InSolverInput = 'IN_SOLVER_INPUT',
  InitIncluded = 'INIT_INCLUDED',
  InUniverse = 'IN_UNIVERSE',
  NewSku = 'NEW_SKU',
  HasCommitments = 'HAS_COMMITMENTS',
  InWorkbenchSection = 'IN_WORKBENCH_SECTION',
  RemovedByUser = 'REMOVED_BY_USER',
  RemovedByTetris = 'REMOVED_BY_TETRIS',
  Excluded = 'EXCLUDED',
  NewSkus = 'NEW_SKUS',
  WithCommitments = 'WITH_COMMITMENTS',
}

export type PogSkuItem = {
  productNum: string;
  labels: ReadonlySet<PogSkuLabel>;
};

export type ClassifyPogSkusInput = {
  onPog: ReadonlyArray<string>;
  workbench: ReadonlyArray<string>;
  clipboardFacings: ReadonlyArray<Pick<FacingGroup, 'product_num'>>;
  solverInput: ReadonlyArray<string>;
  initIncluded: ReadonlyArray<string>;
  excludedIds: ReadonlyArray<string>;
  universe: ReadonlyArray<string>;
  newSkus: ReadonlyArray<string>;
  withCommitments: ReadonlyArray<string>;
};

export type PogSkuClassification = {
  items: Array<PogSkuItem>;
  removedByUser: Array<string>;
  realRemoved: Array<string>;
  foreignRemoved: Array<string>;
  removedByTetris: Array<string>;
  excluded: Array<string>;
  inWorkbench: Array<string>;
  newSkus: Array<string>;
  withCommitments: Array<string>;
};

const unique = (skus: ReadonlyArray<string>): Array<string> => Array.from(new Set(skus));

export const classifyPogSkus = ({
  onPog,
  workbench,
  clipboardFacings,
  solverInput,
  initIncluded,
  excludedIds,
  universe,
  newSkus,
  withCommitments,
}: ClassifyPogSkusInput): PogSkuClassification => {
  const clipboardSkus = clipboardFacings.map(facing => facing.product_num);
  const labelsBySku = new Map<string, Set<PogSkuLabel>>();

  const addFacts = (skus: ReadonlyArray<string>, label: PogSkuLabel) => skus.forEach(sku => {
    const labels = labelsBySku.get(sku) ?? new Set<PogSkuLabel>();

    labels.add(label);
    labelsBySku.set(sku, labels);
  });

  addFacts(onPog, PogSkuLabel.OnPog);
  addFacts(workbench, PogSkuLabel.InWorkbench);
  addFacts(clipboardSkus, PogSkuLabel.InClipboard);
  addFacts(solverInput, PogSkuLabel.InSolverInput);
  addFacts(initIncluded, PogSkuLabel.InitIncluded);
  addFacts(universe, PogSkuLabel.InUniverse);
  addFacts(newSkus, PogSkuLabel.NewSku);
  addFacts(withCommitments, PogSkuLabel.HasCommitments);
  addFacts(excludedIds, PogSkuLabel.Trashed);

  // Legacy POGs without init_included fall back to the solver input, as the backend does.
  const hasInitIncluded = initIncluded.length > 0;

  // Real clipboard facings are never hidden: unlike the other sections this rule ignores the workbench.
  const isRealRemoved = (labels: ReadonlySet<PogSkuLabel>) => labels.has(PogSkuLabel.InClipboard)
    && !labels.has(PogSkuLabel.OnPog)
    && !labels.has(PogSkuLabel.Trashed);
  const isForeignRemoved = (labels: ReadonlySet<PogSkuLabel>) => labels.has(PogSkuLabel.InitIncluded)
    && !labels.has(PogSkuLabel.InSolverInput)
    && !labels.has(PogSkuLabel.OnPog)
    && !labels.has(PogSkuLabel.InWorkbench)
    && !labels.has(PogSkuLabel.Trashed);

  // Each rule may read the section labels set by the rules above it.
  labelsBySku.forEach(labels => {
    const has = (label: PogSkuLabel) => labels.has(label);
    const onPogOrWorkbench = has(PogSkuLabel.OnPog) || has(PogSkuLabel.InWorkbench);

    if (isRealRemoved(labels) || isForeignRemoved(labels)) labels.add(PogSkuLabel.RemovedByUser);

    if (
      has(PogSkuLabel.InSolverInput)
      && (!hasInitIncluded || has(PogSkuLabel.InitIncluded))
      && !has(PogSkuLabel.Trashed)
      && !onPogOrWorkbench
      && !has(PogSkuLabel.RemovedByUser)
    ) labels.add(PogSkuLabel.RemovedByTetris);

    if (
      has(PogSkuLabel.InUniverse)
      && !onPogOrWorkbench
      && !has(PogSkuLabel.RemovedByUser)
      && !has(PogSkuLabel.RemovedByTetris)
    ) labels.add(PogSkuLabel.Excluded);

    if (has(PogSkuLabel.InWorkbench) && !has(PogSkuLabel.OnPog)) labels.add(PogSkuLabel.InWorkbenchSection);
    if (has(PogSkuLabel.NewSku) && !onPogOrWorkbench) labels.add(PogSkuLabel.NewSkus);
    if (has(PogSkuLabel.HasCommitments) && !onPogOrWorkbench) labels.add(PogSkuLabel.WithCommitments);
  });

  const pick = (
    source: ReadonlyArray<string>,
    predicate: (labels: ReadonlySet<PogSkuLabel>) => boolean
  ): Array<string> => unique(source).filter(sku => predicate(labelsBySku.get(sku) as ReadonlySet<PogSkuLabel>));
  const pickLabelled = (source: ReadonlyArray<string>, label: PogSkuLabel) => pick(source, labels => labels.has(label));

  const realRemoved = pick(clipboardSkus, isRealRemoved);
  const foreignRemoved = pick(initIncluded, isForeignRemoved);

  return {
    items: Array.from(labelsBySku, ([productNum, labels]) => ({ productNum, labels })),
    removedByUser: unique([...realRemoved, ...foreignRemoved]),
    realRemoved,
    foreignRemoved,
    removedByTetris: pickLabelled(solverInput, PogSkuLabel.RemovedByTetris),
    excluded: pickLabelled(universe, PogSkuLabel.Excluded),
    inWorkbench: pickLabelled(workbench, PogSkuLabel.InWorkbenchSection),
    newSkus: pickLabelled(newSkus, PogSkuLabel.NewSkus),
    withCommitments: pickLabelled(withCommitments, PogSkuLabel.WithCommitments),
  };
};

export const dropStaleClipboardFacings = <T extends Pick<FacingGroup, 'product_num'>>(
  facings: ReadonlyArray<T>,
  onPogSkus: ReadonlyArray<string>,
  excludedIds: ReadonlyArray<string>
): Array<T> => {
  const staleSkus = new Set([...onPogSkus, ...excludedIds]);

  return facings.filter(facing => !staleSkus.has(facing.product_num));
};

export const buildVirtualClipboardFacing = (
  sku: string,
  packaging: Packaging,
  aisleDepth: number
): FacingGroup => {
  const fixtureType = packaging.peggable ? 'PEGBOARD' : 'SHELF';
  let orientation = 0;
  let orientedPackaging: Packaging = { ...packaging };

  if (orientedPackaging.depth > aisleDepth) {
    orientation = 2;
    orientedPackaging = {
      ...orientedPackaging,
      ...orientationFacing(orientation, packaging.width, packaging.height, packaging.depth),
    };

    if (orientedPackaging.depth > aisleDepth) {
      orientation = 4;
      orientedPackaging = {
        ...orientedPackaging,
        ...orientationFacing(orientation, packaging.width, packaging.height, packaging.depth),
      };
    }
  }

  return {
    located_fixture_id: 0,
    x: 0,
    y: 0,
    horizontal_facings: 1,
    vertical_facings: 1,
    depth_facings: 1,
    orientation_code: orientation,
    segment: 0,
    cap_nested: '',
    capped_horizontal_facings: 0,
    capped_vertical_facings: 0,
    capped_depth_facings: 0,
    capped_orientation_code: 0,
    peg_id: null,
    product_num: sku.toString(),
    packaging: orientedPackaging,
    defaultPackaging: packaging,
    id: generateUniqueId(),
    cappedPackaging: {
      width: 0,
      height: 0,
      depth: 0
    },
    fixtureType,
    esl_peg_id: null
  };
};
